/**
 * The save control on a 词典 entry, for a reader who has a session.
 *
 * That page is served from the edge, one set of bytes to everybody, so the
 * button arrives saying `Save` whether or not the reader already holds the
 * word. Two things happen here and neither is needed for the control to work.
 * The buttons for words this reader holds are relabelled, and a press posts
 * where it stands instead of landing the reader on their list.
 *
 * **Nothing here is load-bearing.** With the script blocked, the form posts,
 * `CiPage` turns the word over and answers with `/user/ci/`, which is the list
 * with the word in it. That is the arrangement every account control on this
 * site keeps, and `src/site/download-offer.ts` states it for the vocabulary
 * downloads. What the script buys is the reader staying on the page they were
 * reading.
 *
 * **A signed-out reader is offered an account in place.** The offer is in the
 * markup and `hidden`, and a press reveals it rather than taking the reader to
 * Cognito. The sign-in link inside it is the one `src/site/sign-in-link.ts`
 * puts the current page on.
 *
 * **It is revealed on a press rather than on sight**, which is where this parts
 * company with the vocabulary downloads. #499 turns those two links off for a
 * reader the page can see is signed out, on the grounds that the file is what
 * they came for and a dead link with a reason beats a download that fails. A
 * save control is not what anybody came to a 词典 entry for, and an account
 * offer drawn under every one of 22,332 entries would be furniture on every
 * page of the section.
 *
 * The other two thirds of this are `save-word-control.ts`, which is the markup,
 * and `save-word-route.ts`, which is what the handler is asked.
 */

import { buttonOf, drawnAsHeld, drawSavedWord } from "./save-word-control.js";
import { askHeldWords } from "./save-word-route.js";
import { boundOnce } from "./bound-once.js";
import { listenForSavedWord } from "./save-word-press.js";
import { openWhereRoom } from "./study-standing-badge.js";
import type { SaveWordsOptions } from "./save-word-options.js";

/** The block holding one word's form and the offer under it. */
const controlSelector = ".cb-save-word:not(.cb-save-liju)";

/** The controls this bundle has not already bound. See `unbound`. */
const notYetListening = boundOnce();

/**
 * What `head/session-state.html` writes on the document for a reader carrying a
 * session.
 *
 * The test is against this value rather than against its absence, in the shape
 * `download-offer.ts` takes. A page whose head script never ran carries no
 * attribute, and the fallback for a signed-in reader is the form posting the
 * way it does with no script at all.
 */
const signedIn = "in";

/** Whether the page says there is a reader to ask the route about. */
export function hasSession(document: Document): boolean {
  return document.documentElement.dataset["cbSession"] === signedIn;
}

/**
 * Say which of the words on this page the reader already holds.
 *
 * Asked only about the buttons the page could not say for itself. A control
 * drawn `held` is one `/user/ci/` wrote out of the reader's own session, so the
 * route would only hand back the word it was given.
 * @returns The buttons drawn as held.
 */
async function markHeld(
  buttons: readonly HTMLButtonElement[],
  fetched: typeof fetch,
): Promise<readonly HTMLButtonElement[]> {
  const held =
    (await askHeldWords(
      fetched,
      buttons.map((button) => button.value),
    )) ?? [];
  const drawn = buttons.filter((one) => held.includes(one.value));

  for (const button of drawn) {
    drawSavedWord(button, true);
  }

  return drawn;
}

/**
 * Takes the presses on every save control on the page.
 *
 * Returns on a page with no control, which is every page of this site but the
 * 词典 entries, the search results and `/user/ci/`.
 *
 * **Safe to call again over a region that has just been replaced**, which is
 * what `/user/ci/` needs: htmx swaps the list on a tag, a restore, a filter and
 * every page of it, and the hundred controls inside arrive with no listener.
 * `options.within` is the region, and a control that is already listening is
 * skipped, so nothing is bound twice however the two calls overlap.
 * @param document - The page the bundle is running on.
 * @param fetched - What asks the route, which a test hands its own.
 * @param options - Page-specific additions to the shared control.
 */
export function startSaveWords(
  document: Document,
  fetched: typeof fetch = globalThis.fetch,
  options: SaveWordsOptions = {},
): void {
  const controls = notYetListening(options.within ?? document, controlSelector);
  const buttons = controls
    .map((control) => buttonOf(control))
    .filter((button) => button !== null);

  if (buttons.length === 0) {
    return;
  }

  const panels = new Map(
    controls.map((control) => [
      buttonOf(control),
      listenForSavedWord({
        control,
        signedIn: () => hasSession(document),
        fetched,
        options,
      }),
    ]),
  );
  const asking = buttons.filter((button) => !drawnAsHeld(button));

  // A badge found held opens its panel where there is room. See
  // `study-standing.ts`.
  if (options.ask !== false && hasSession(document) && asking.length > 0) {
    void markHeld(asking, fetched)
      .then(async (held) => {
        await Promise.all(
          held.map(async (button) =>
            openWhereRoom(panels.get(button), document),
          ),
        );
      })
      .catch(() => undefined);
  }
}
